import { useId } from 'react'
import { useDropdown } from '../utils/DropdownContext'

type Option = { title: string; value: string }

interface MultiSelectProps {
  label?: string
  options: Option[]
  /** Controlled — always an array; pass `[]` for "nothing selected". */
  value: string[]
  onChange?: (values: string[]) => void
  placeholder?: string
  className?: string
  disabled?: boolean
  /** Shown inside the open panel when `options` is empty. */
  emptyMessage?: string
}

/**
 * Multi-select picker matching `DropDown`/`CategorySelect`: same 38px control, same
 * `bg-muted` + `#D9D9D9` border, same chevron/checkmark glyphs and the same `bg-card`
 * panel. Selection-only — the taxonomy rows themselves are managed on the Product
 * Metadata page.
 *
 * Open state is owned by the shared `DropdownProvider` (one open dropdown per tree),
 * exactly like `DropDown` and `CategorySelect`: `useDropdown()` throws when no provider
 * is mounted, and a render-time throw here unwinds to the root — so this component is
 * only ever rendered inside a `DropdownProvider`. Unlike the single-selects, picking an
 * option does NOT close the panel, so several can be ticked in one pass.
 */
export const MultiSelect = ({
  label,
  options,
  value,
  onChange,
  placeholder = 'Select options',
  className = '',
  disabled = false,
  emptyMessage = 'Nothing to choose from yet.',
}: MultiSelectProps) => {
  const dropdownId = useId()
  const { openDropdown, setOpenDropdown } = useDropdown()
  const showDrop = openDropdown === dropdownId

  const selected = value ?? []
  const selectedTitles = options.filter(o => selected.includes(o.value)).map(o => o.title)
  const displayText = selectedTitles.length > 0 ? selectedTitles.join(', ') : placeholder

  const toggle = (optionValue: string) => {
    const next = selected.includes(optionValue)
      ? selected.filter(v => v !== optionValue)
      : [...selected, optionValue]
    onChange?.(next)
  }

  return (
    <div>
      {label && <label className="block text-[14px] font-[600] text-foreground mb-1">{label}</label>}
      <div className={`relative ${className}`}>
        <div
          className={`h-[38px] rounded-[10px] flex items-center justify-between gap-2 p-2 bg-muted border-[1px] border-[#D9D9D9] ${
            disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-muted'
          }`}
          onClick={() => {
            if (!disabled) setOpenDropdown(showDrop ? null : dropdownId)
          }}
        >
          <span
            className={`text-[14px] truncate ${
              selectedTitles.length > 0 ? 'text-foreground' : 'text-muted-foreground'
            }`}
          >
            {displayText}
          </span>
          <svg
            width="12"
            height="7"
            viewBox="0 0 12 7"
            fill="none"
            className={`shrink-0 transform transition-transform ${showDrop ? 'rotate-180' : ''}`}
          >
            <path
              d="M10.1693 1.3335L6.0026 5.50016L1.83594 1.3335"
              stroke="#3F3E42"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        {showDrop && (
          <div
            className="absolute top-full left-0 right-0 mt-2 bg-card rounded-[10px] p-3 z-10 max-h-[240px] overflow-y-auto"
            style={{ boxShadow: '0px 13px 15px 5px #0302084D' }}
          >
            {options.length === 0 && (
              <p className="px-2 py-2 text-[14px] text-muted-foreground">{emptyMessage}</p>
            )}
            {options.map(option => (
              <div
                key={option.value}
                className="flex justify-between items-center gap-2 p-2 hover:bg-muted rounded cursor-pointer"
                onClick={() => toggle(option.value)}
              >
                <span className="text-foreground font-[400] text-[16px]">{option.title}</span>
                {selected.includes(option.value) && (
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="shrink-0">
                    <path
                      d="M13.3307 4L5.9974 11.3333L2.66406 8"
                      stroke="#3F3E42"
                      strokeWidth="1.33333"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default MultiSelect
